import React from 'react';
import { Tooltip } from 'antd';
import {
  ArrowsAltOutlined,
  AlignCenterOutlined,
  AlignLeftOutlined,
  BgColorsOutlined,
  BoldOutlined,
  BorderOutlined,
  ColumnWidthOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LayoutOutlined,
  LinkOutlined,
  PictureOutlined,
  RadiusSettingOutlined,
  UnderlineOutlined,
  FontColorsOutlined,
  FileTextOutlined,
  FormOutlined,
  ColumnHeightOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';

export function iconLabel(title: string, icon: React.ReactNode): React.ReactNode {
  return (
    <Tooltip title={title} mouseEnterDelay={0.35}>
      <span
        className="icon-form-label"
        style={{ display: 'inline-flex', alignItems: 'center', fontSize: 15, lineHeight: 1 }}
        aria-label={title}
      >
        {icon}
      </span>
    </Tooltip>
  );
}

export function StyleFieldGroup({
  label,
  children,
  className,
}: {
  label?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={['style-field-group', className].filter(Boolean).join(' ')}>
      {label ? <div className="style-field-group__label">{label}</div> : null}
      <div className="style-field-group__items">{children}</div>
    </div>
  );
}

export function IconFieldRow({
  label,
  children,
  block,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  block?: boolean;
}) {
  return (
    <div className={`icon-field-row${block ? ' icon-field-row--block' : ''}`}>
      <span className="icon-field-row__label">{label}</span>
      <div className="icon-field-row__control">{children}</div>
    </div>
  );
}

export const SectionLabel = {
  colors: iconLabel('Colors', <BgColorsOutlined />),
  typography: iconLabel('Text style', <FontSizeOutlined />),
  border: iconLabel('Border', <BorderOutlined />),
  spacing: iconLabel('Spacing', <ColumnHeightOutlined />),
  pagePadding: iconLabel('Page padding (px)', <ArrowsAltOutlined />),
  layout: iconLabel('Layout', <LayoutOutlined />),
  text: iconLabel('Text', <FileTextOutlined />),
  image: iconLabel('Image', <PictureOutlined />),
  displayName: iconLabel('Display name', <FormOutlined />),
  page: iconLabel('Page layout', <LayoutOutlined />),
  header: iconLabel('Header style', <FontSizeOutlined />),
  line: iconLabel('Line style', <FileTextOutlined />),
  nestedLine: iconLabel('Nested line (level 1)', <FileTextOutlined />),
  deepNestedLine: iconLabel('Nested line (level 2+)', <FileTextOutlined />),
  links: iconLabel('Link style', <LinkOutlined />),
};

export const FieldLabel = {
  textColor: iconLabel('Text color', <FontColorsOutlined />),
  backgroundColor: iconLabel('Background color', <BgColorsOutlined />),
  linkColor: iconLabel('Link color', <LinkOutlined />),
  fontSize: iconLabel('Font size', <FontSizeOutlined />),
  fontFamily: iconLabel('Font type (family)', <FontSizeOutlined />),
  fontWeight: iconLabel('Font weight', <BoldOutlined />),
  borderWidth: iconLabel('Border width', <BorderOutlined />),
  borderStyle: iconLabel('Border style', <BorderOutlined />),
  borderColor: iconLabel('Border color', <BorderOutlined />),
  borderRadius: iconLabel('Border radius', <RadiusSettingOutlined />),
  padding: iconLabel('Padding (all)', <ArrowsAltOutlined />),
  paddingLeft: iconLabel('Padding left', <AlignLeftOutlined />),
  paddingRight: iconLabel('Padding right', <AlignLeftOutlined style={{ transform: 'scaleX(-1)' }} />),
  paddingTop: iconLabel('Padding top', <ColumnHeightOutlined />),
  paddingBottom: iconLabel('Padding bottom', <ColumnHeightOutlined style={{ transform: 'scaleY(-1)' }} />),
  margin: iconLabel('Margin (all)', <ArrowsAltOutlined />),
  marginLeft: iconLabel('Margin left', <AlignLeftOutlined />),
  marginRight: iconLabel('Margin right', <AlignLeftOutlined style={{ transform: 'scaleX(-1)' }} />),
  marginTop: iconLabel('Margin top', <ColumnHeightOutlined />),
  marginBottom: iconLabel('Margin bottom', <ColumnHeightOutlined style={{ transform: 'scaleY(-1)' }} />),
  align: iconLabel('Align', <AlignCenterOutlined />),
  textAlign: iconLabel('Text align', <AlignCenterOutlined />),
  width: iconLabel('Width', <ColumnWidthOutlined />),
  imageSrc: iconLabel('Image source', <PictureOutlined />),
  imageFit: iconLabel('Image fit', <PictureOutlined />),
  content: iconLabel('Text', <FileTextOutlined />),
  displayName: iconLabel('Title / display name', <FormOutlined />),
  templateName: iconLabel('Template name', <FileTextOutlined />),
  template: iconLabel('Template', <AppstoreOutlined />),
  active: iconLabel('Active template', <CheckCircleOutlined />),
  value: iconLabel('Value', <FormOutlined />),
  selectOption: iconLabel('Select option', <FormOutlined />),
  key: iconLabel('Key', <FormOutlined />),
  defaultTextColor: iconLabel('Default text color', <FontColorsOutlined />),
  defaultBackgroundColor: iconLabel('Default page/background color', <BgColorsOutlined />),
  defaultLinkColor: iconLabel('Default link color', <LinkOutlined />),
};

export function modalIconTitle(title: string, icon: React.ReactNode): React.ReactNode {
  return (
    <span className="style-panel-title" aria-label={title}>
      <span className="style-panel-title__icon" aria-hidden>
        {icon}
      </span>
      <span className="style-panel-title__text">{title}</span>
    </span>
  );
}
